import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Maximize2, X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useDisplayHeart } from '../hooks/useDisplayHeart';
import { CHART_OVERLAY_HOST_ID } from '../constants/chartOverlay';
import liveChartStream from '../services/liveChartStream';

interface TechnicalSentimentsProps {
  selectedSymbol?: string;
  isRightPanelVisible?: boolean;
}

/** How long the needle rests on Neutral after a candle closes. */
const NEUTRAL_PAUSE_MS = 2500;

/** Ticks are far too frequent to render individually, so their effect is flushed on a timer. */
const TICK_FLUSH_MS = 180;

/**
 * The needle's travel on each side. It stops short of the very end of the arc so there is
 * somewhere left to move, and short of the middle so the side it is on is never ambiguous.
 */
const SIDE_INNER = 40;
const SIDE_OUTER = 6;
const NEUTRAL_VALUE = 50;

/** How far one flush of net ticks pushes the needle, and how strongly it drifts back. */
const TICK_STEP = 0.07;
const TICK_DECAY = 0.05;

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

interface GaugeProps {
  sentiment: 'Sell' | 'Neutral' | 'Buy';
  color: string;
  needleAngle: number;
  slowSweep: boolean;
  isDark: boolean;
  /** Enlarged copies scale everything up; the panel copy keeps its original proportions. */
  large?: boolean;
}

/**
 * The half-circle gauge itself.
 *
 * Split out so the panel and the enlarged overlay are the same drawing at two sizes rather
 * than two drawings that have to be kept in step. Everything is laid out in the viewBox's
 * coordinates and scaled from there, so the two sizes cannot drift apart.
 *
 * The arc's centre is (100, ARC_CENTRE_Y) with a radius of 70, which puts its top edge at
 * y=27 and its ends at y=97. The labels are placed off those edges rather than at round
 * numbers, so the clearances stay right if the arc ever moves.
 */
const ARC_CENTRE_Y = 97;
const ARC_STROKE = 6;
const LABEL_SIZE = 11;
/** Clear air between a label and the arc, so neither the ends nor the top are crowded. */
const LABEL_GAP = 10;

/** Sell and Buy sit below the arc's ends; the gap is measured from the stroke's outer edge. */
const SIDE_LABEL_Y = ARC_CENTRE_Y + ARC_STROKE / 2 + LABEL_GAP + LABEL_SIZE * 0.75;
/** Neutral sits above the arc's crown, its baseline the same clearance clear of it. */
const TOP_LABEL_Y = ARC_CENTRE_Y - 70 - ARC_STROKE / 2 - LABEL_GAP;

const ARC_PATH = `M 30 ${ARC_CENTRE_Y} A 70 70 0 0 1 170 ${ARC_CENTRE_Y}`;

const SentimentGauge: React.FC<GaugeProps> = ({
  sentiment,
  color,
  needleAngle,
  slowSweep,
  isDark,
  large = false
}) => (
  <div className={`relative w-full flex justify-center items-center ${large ? 'flex-1 min-h-0' : 'h-40 mb-0'}`}>
    <svg
      viewBox="0 0 200 122"
      preserveAspectRatio="xMidYMid meet"
      className={large ? 'w-full h-full max-h-[70vh]' : 'absolute top-[8px]'}
      width={large ? undefined : 200}
      height={large ? undefined : 122}
    >
      <defs>
        <linearGradient id={`sentimentGradient-${large ? 'lg' : 'sm'}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#ef4444" />
          <stop offset="50%" stopColor="#6b7280" />
          <stop offset="100%" stopColor="#22c55e" />
        </linearGradient>
      </defs>

      {/* Background arc */}
      <path
        d={ARC_PATH}
        fill="none"
        stroke={isDark ? '#374151' : '#e5e7eb'}
        strokeWidth={ARC_STROKE}
        strokeLinecap="round"
      />

      {/* Gradient arc from red (left) to green (right) */}
      <path
        d={ARC_PATH}
        fill="none"
        stroke={`url(#sentimentGradient-${large ? 'lg' : 'sm'})`}
        strokeWidth={ARC_STROKE}
        strokeLinecap="round"
      />

      {/* The needle, pivoting on the centre of the arc above so its tip sweeps parallel to it.
          
          Only the needle turns. The hub used to be inside the rotating group and appeared to
          wander off its spot with every swing: `rotate(angle cx cy)` is a translate, a turn and
          a translate back, and the browser animates that by interpolating the combined matrix —
          which walks the pivot along a straight line between the two positions instead of
          leaving it where it is. Turning about a fixed `transform-origin` interpolates the angle
          itself, and the hub is drawn separately besides, so it cannot move whatever the
          browser decides to do with the transform. */}
      <g
        style={{
          transform: `rotate(${needleAngle}deg)`,
          transformBox: 'view-box',
          transformOrigin: `100px ${ARC_CENTRE_Y}px`,
          // Swinging to and from Neutral is a deliberate move and reads better slowly;
          // the tick jitter is continuous and would lag badly at the same duration.
          transition: `transform ${slowSweep ? 700 : 260}ms ease-out`
        }}
      >
        <line
          x1="100"
          y1={ARC_CENTRE_Y}
          x2="100"
          y2={ARC_CENTRE_Y - 47}
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
        />
      </g>
      <circle cx="100" cy={ARC_CENTRE_Y} r="3" fill={color} />

      <text x="30" y={SIDE_LABEL_Y} textAnchor="middle" fontSize={LABEL_SIZE} fill="#6b7280" fontWeight="500">
        Sell
      </text>
      <text x="100" y={TOP_LABEL_Y} textAnchor="middle" fontSize={LABEL_SIZE} fill="#6b7280" fontWeight="500">
        Neutral
      </text>
      <text x="170" y={SIDE_LABEL_Y} textAnchor="middle" fontSize={LABEL_SIZE} fill="#6b7280" fontWeight="500">
        Buy
      </text>
    </svg>

    <span className="sr-only">{sentiment}</span>
  </div>
);

const TechnicalSentiments: React.FC<TechnicalSentimentsProps> = ({ selectedSymbol }) => {
  const { theme } = useTheme();

  // The heart the gauge shows: the strategy's own settings while the AI is running for this
  // symbol, and the hourly heart otherwise. `useDisplayHeart` holds that rule, and the marker
  // drawn on the chart reads through it too, so the two always agree.
  const heart = useDisplayHeart(selectedSymbol);

  // Position within the current side: 0 sits next to Neutral, 1 at the far end.
  const [bias, setBias] = useState(0.5);
  const [restingOnNeutral, setRestingOnNeutral] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const trendRef = useRef<boolean | null>(heart.isTrendUp);
  trendRef.current = heart.isTrendUp;

  const lastTradePriceRef = useRef<number | null>(null);
  const netTicksRef = useRef(0);

  // Pause on Neutral each time a bar closes, then return to whichever side the heart is on.
  useEffect(() => {
    if (!heart.barTime || !heart.ready) return;

    setRestingOnNeutral(true);
    const timer = setTimeout(() => setRestingOnNeutral(false), NEUTRAL_PAUSE_MS);
    return () => clearTimeout(timer);
  }, [heart.barTime, heart.ready]);

  // Live trades nudge the needle around within its side. They are counted here and applied
  // on a timer rather than per trade, because a busy pair prints many every second and each
  // one would otherwise be a React render.
  useEffect(() => {
    if (!selectedSymbol) return;

    lastTradePriceRef.current = null;
    netTicksRef.current = 0;

    const unsubscribe = liveChartStream.subscribe(selectedSymbol, heart.interval, {
      onTrade: trade => {
        const previous = lastTradePriceRef.current;
        lastTradePriceRef.current = trade.price;
        if (previous === null || trade.price === previous) return;
        netTicksRef.current += trade.price > previous ? 1 : -1;
      }
    });

    const flush = setInterval(() => {
      const net = netTicksRef.current;
      netTicksRef.current = 0;

      const trendUp = trendRef.current;
      if (trendUp === null) return;

      setBias(previous => {
        // A tick in the heart's favour pushes toward the far end of its side, one against
        // it eases back toward Neutral. Either way the needle stays on the heart's side.
        const inFavour = trendUp ? net : -net;
        const moved = previous + Math.sign(inFavour) * Math.min(Math.abs(inFavour), 3) * TICK_STEP;
        const next = clamp01(moved + (0.5 - moved) * TICK_DECAY);
        // Returning the previous value lets React skip the render, so a quiet market does
        // not cost one every flush.
        return Math.abs(next - previous) < 0.002 ? previous : next;
      });
    }, TICK_FLUSH_MS);

    return () => {
      unsubscribe();
      clearInterval(flush);
    };
  }, [selectedSymbol, heart.interval]);

  const showNeutral = heart.isTrendUp === null || restingOnNeutral;
  const sentiment: 'Sell' | 'Neutral' | 'Buy' = showNeutral ? 'Neutral' : heart.isTrendUp ? 'Buy' : 'Sell';

  const sentimentValue = useMemo(() => {
    if (showNeutral) return NEUTRAL_VALUE;

    const travel = SIDE_INNER - SIDE_OUTER;
    // Buy mirrors Sell about the centre of the gauge.
    return heart.isTrendUp ? 100 - (SIDE_INNER - bias * travel) : SIDE_INNER - bias * travel;
  }, [showNeutral, heart.isTrendUp, bias]);

  // The gauge is a half circle and the needle is drawn pointing straight up, so the rotation
  // needed is -90° to reach Sell on the left, 0° for Neutral at the top and +90° for Buy on
  // the right.
  const needleAngle = (sentimentValue / 100) * 180 - 90;

  const color = sentiment === 'Sell' ? '#ef4444' : sentiment === 'Buy' ? '#22c55e' : '#6b7280';

  const caption = () => {
    if (!selectedSymbol) return 'No symbol selected';
    if (!heart.ready) return `Loading ${heart.interval} candles…`;
    if (heart.isTrendUp === null) return `Waiting for a ${heart.interval} band cross`;
    if (restingOnNeutral) return `${heart.interval} candle closed`;

    const source = heart.followingStrategy ? 'strategy' : 'default';
    return `${heart.isTrendUp ? 'Green' : 'Red'} heart ${heart.level?.toFixed(2)} • ${heart.interval} ${source}`;
  };

  const gauge = (large: boolean) => (
    <SentimentGauge
      sentiment={sentiment}
      color={color}
      needleAngle={needleAngle}
      slowSweep={restingOnNeutral}
      isDark={theme === 'dark'}
      large={large}
    />
  );

  // The enlarged copy is portalled into the chart so it can take exactly the chart's area.
  // Rendering it in place and positioning it over the chart would have meant guessing at
  // that area from the far side of the layout, and re-guessing whenever a panel opened.
  //
  // The chart mounts on its own schedule, so the host is looked for until it turns up rather
  // than once on the first render. Finding it re-runs this and the polling stops.
  const [overlayHost, setOverlayHost] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (overlayHost?.isConnected) return;

    const look = () => {
      const node = document.getElementById(CHART_OVERLAY_HOST_ID);
      if (node) setOverlayHost(node);
    };

    look();
    const timer = setInterval(look, 500);
    return () => clearInterval(timer);
  }, [overlayHost]);

  return (
    <div className="py-2 pt-0">
      <div
        className={`group/meter theme-bg-secondary bg-opacity-95 p-3 py-3 rounded-lg z-10 shadow-xs relative ${
          theme === 'light' ? 'border-gray-200' : 'border-gray-800'
        }`}
      >
        {/* Enlarge. Kept out of the way until the panel is hovered, the way the panels' own
            close buttons are. */}
        {overlayHost && (
          <button
            onClick={() => setIsExpanded(true)}
            title="Show this meter over the chart"
            className="absolute top-1 right-1 z-20 p-1 rounded-sm opacity-0 transition-opacity duration-200 group-hover/meter:opacity-100 focus-visible:opacity-100 theme-text-quaternary hover:theme-text-primary"
          >
            <Maximize2 className="w-3 h-3" />
          </button>
        )}

        {gauge(false)}

        {/* Current Sentiment */}
        <div className="text-center -mt-5">
          <div className="text-xs font-bold" style={{ color }}>
            {sentiment}
          </div>
          <div className="text-xs theme-text-quaternary mt-1">{caption()}</div>
        </div>
      </div>

      {isExpanded &&
        overlayHost &&
        createPortal(
          <div
            className="absolute inset-0 z-30 flex flex-col backdrop-blur-sm"
            style={{ backgroundColor: theme === 'dark' ? 'rgba(30, 41, 59, 0.94)' : 'rgba(255, 255, 255, 0.94)' }}
            role="dialog"
            aria-label="Sentiment meter"
          >
            <div className="flex items-center justify-between px-3 py-2 flex-shrink-0">
              <span className="text-xs theme-text-quaternary">{selectedSymbol}</span>
              <button
                onClick={() => setIsExpanded(false)}
                title="Close"
                className="p-1 rounded-sm theme-text-quaternary hover:theme-text-primary"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {gauge(true)}

            <div className="text-center pb-6 flex-shrink-0">
              <div className="text-2xl font-bold" style={{ color }}>
                {sentiment}
              </div>
              <div className="text-xs theme-text-quaternary mt-1">{caption()}</div>
            </div>
          </div>,
          overlayHost
        )}
    </div>
  );
};

export default TechnicalSentiments;
